import { Alert, Button, Col, Flex, Input, Row, Space, Typography } from 'antd';
import { FC, memo } from 'react';
import { LightningChargeFill, TrashFill } from 'react-bootstrap-icons';

import { DomainVisitors } from '@/modules/Domains';
import { ParsedRedirectRow, RedirectMatchMode } from '@/modules/Sites/Redirects';

import { RedirectsConfig } from '../RedirectsConfig';
import { RedirectsBulkPreviewTable } from './RedirectsBulkPreviewTable';

interface Props {
  text: string;
  rows: ParsedRedirectRow[];
  validCount: number;
  errorCount: number;
  isLoading: boolean;
  isParsePending: boolean;
  statusCode: number;
  matchMode: RedirectMatchMode;
  forVisitor: DomainVisitors;
  isActive: boolean;
  onChangeText: (text: string) => void;
  onChangeStatusCode: (value: number) => void;
  onChangeMatchMode: (value: RedirectMatchMode) => void;
  onChangeForVisitor: (value: DomainVisitors) => void;
  onChangeIsActive: (value: boolean) => void;
  onSubmit: () => void;
  onClear: () => void;
}

export const RedirectsBulkCreateView: FC<Props> = memo(({
  text,
  onChangeText,
  statusCode,
  onChangeStatusCode,
  matchMode,
  onChangeMatchMode,
  forVisitor,
  onChangeForVisitor,
  isActive,
  onChangeIsActive,
  rows,
  validCount,
  errorCount,
  isLoading,
  isParsePending,
  onSubmit,
  onClear,
}) => {
  return (
    <Flex vertical gap={ 16 }>
      <Alert
        type="info"
        showIcon
        message="Вставте список редіректів — по одному в рядку"
        description={
          <Typography.Text type="secondary">
            Формат: <Typography.Text code>/звідки - /куди</Typography.Text>. Роздільник —
            дефіс з пробілами навколо (<Typography.Text code> - </Typography.Text>). Шлях «З» має відповідати
            наявній сторінці сайту. Налаштування «Для відвідувача» та «Активний» застосуються до всіх
            створених редіректів.
          </Typography.Text>
        }
      />

      <Row gutter={ [16, 16] }>
        <Col xs={ 24 } lg={ 11 }>
          <Flex vertical gap={ 12 }>
            <RedirectsConfig
              forDomainVisitor={ forVisitor }
              active={ isActive }
              statusCode={ statusCode }
              matchMode={ matchMode }
              onChangeDomainVisitor={ onChangeForVisitor }
              onChangeActivity={ onChangeIsActive }
              onChangeStatusCode={ onChangeStatusCode }
              onChangeMatchMode={ onChangeMatchMode }
              disabled={ isLoading }
            />

            <Input.TextArea
              value={ text }
              onChange={ (event) => onChangeText(event.target.value) }
              placeholder={ '/page1 - /page2' }
              disabled={ isLoading }
              autoSize={ { minRows: 14, maxRows: 22 } }
              style={{ fontFamily: 'monospace' }}
            />
          </Flex>
        </Col>

        <Col xs={ 24 } lg={ 13 }>
          <RedirectsBulkPreviewTable rows={ rows } loading={ isParsePending }/>
        </Col>
      </Row>

      <Flex justify="space-between" align="center" wrap="wrap" gap={ 8 }>
        <Space size={ 16 }>
          <Typography.Text type="secondary">Розпізнано: <b>{ rows.length }</b></Typography.Text>
          <Typography.Text type="success">До створення: <b>{ validCount }</b></Typography.Text>
          { errorCount > 0 && <Typography.Text type="danger">Немає сторінки: <b>{ errorCount }</b></Typography.Text> }
        </Space>

        <Space>
          <Button icon={ <TrashFill/> } onClick={ onClear } disabled={ isLoading || !text }>
            Очистити
          </Button>
          <Button
            type="primary"
            icon={ <LightningChargeFill/> }
            loading={ isLoading }
            disabled={ validCount === 0 }
            onClick={ onSubmit }
          >
            Створити
          </Button>
        </Space>
      </Flex>
    </Flex>
  );
});
